/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, ChangeEvent, useContext, useEffect } from 'react';
import {
  Typography, Button, Grid, DialogTitle, Dialog, DialogContent,
  IconButton, Snackbar, Alert, Tabs, Tab, Box, Tooltip,
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { UserContext } from '../../../components/shared/useUser';
import { usePosMappingsContext } from '../context/PosMappingsContext';
import { exportFile } from '../../../utils';
import useLogError from '../../../hooks/useLogError';
import { X } from '../../../assets';
import { Loader } from '../../../components/ui/Loader';
import PosMappingValidationTable, { PosMappingImportRowError } from './PosMappingValidationTable';

type ValidationErrors = {
  ParseErrors: PosMappingImportRowError[];
  SkuValidation: PosMappingImportRowError[];
  ConflictErrors: PosMappingImportRowError[];
};

const VALIDATION_TABS = ['ParseErrors', 'SkuValidation', 'ConflictErrors'] as const;

const TAB_DESCRIPTION_KEYS = [
  'bulk.ParseErrorsDesc',
  'bulk.SkuValidationDesc',
  'bulk.ConflictErrorsDesc',
] as const;

const ERRORS_PAGE_SIZE = 5;

interface PosMappingsBulkManageProps {
  open: boolean;
  handleClose: () => void;
  onSuccess: (message: string) => void;
}

const PosMappingsBulkManage: React.FC<PosMappingsBulkManageProps> = ({ open, handleClose, onSuccess }) => {
  const { t } = useTranslation('posMapping');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { exportPosMappings, importPosMappings } = useNswagClient();
  const { selectedStore } = useContext(UserContext);
  const { storeSearch } = usePosMappingsContext();
  const { logError } = useLogError();
  const [showUploadSuccessMessage, setShowUploadSuccessMessage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [tabPages, setTabPages] = useState<Record<number, number>>({ 0: 0, 1: 0, 2: 0 });

  useEffect(() => {
    if (!open) {
      setErrorMessage(null);
      setSuccessMessage(null);
      setSelectedFile(null);
      setValidationErrors(null);
      setActiveTab(0);
      setTabPages({ 0: 0, 1: 0, 2: 0 });
    }
  }, [open]);

  const getTabItems = (tabIndex: number): PosMappingImportRowError[] => {
    if (!validationErrors) return [];
    return validationErrors[VALIDATION_TABS[tabIndex]] ?? [];
  };

  const getTotalPages = (tabIndex: number): number => {
    return Math.ceil(getTabItems(tabIndex).length / ERRORS_PAGE_SIZE);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setErrorMessage(null);
    setValidationErrors(null);
    const file = event.target.files?.[0] ?? null;

    if (file) {
      const fileType = file.name.split('.').pop();
      if (fileType !== 'xlsx') {
        setSuccessMessage(null);
        setErrorMessage(t('bulk.errorInvalidFile'));
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setSuccessMessage(t('bulk.fileSelected') + `: ${file.name}`);
    }

    event.target.value = '';
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsExporting(true);

    const franchiseId = selectedStore?.franchiseId;

    if (!franchiseId) {
      setErrorMessage(t('selectfranchise'));
      setIsExporting(false);
      return;
    }

    const store = storeSearch || undefined;

    exportPosMappings(franchiseId, store)
      .then((response) => {
        if (response) {
          exportFile(
            response.data,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'POSMappings.xlsx',
          );
        }
      })
      .catch(logError)
      .finally(() => {
        setIsExporting(false);
        handleClose();
      });
  };

  const handleSave = async () => {
    if (!selectedFile) {
      setErrorMessage(t('bulk.errorInvalidFile'));
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);
    setValidationErrors(null);

    try {
      const franchiseId = selectedStore?.franchiseId ?? 0;
      await importPosMappings(franchiseId, selectedFile);
      setShowUploadSuccessMessage(true);
      setSelectedFile(null);
      setSuccessMessage(null);
      onSuccess(t('bulk.uploadSuccess'));
    } catch (error: any) {
      const data = error?.responseData
        ?? (() => { try { return JSON.parse(error?.message); } catch { return null; } })();

      if (
        data &&
        (data.ParseErrors !== undefined ||
          data.SkuValidation !== undefined ||
          data.ConflictErrors !== undefined)
      ) {
        setValidationErrors(data as ValidationErrors);
        setActiveTab(0);
        setTabPages({ 0: 0, 1: 0, 2: 0 });
      } else {
        logError(error);
        setErrorMessage(t('bulk.errorUploadingFile'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setShowUploadSuccessMessage(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="pos-bulk-modal-title"
        aria-describedby="pos-bulk-modal-description"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <InventoryIcon
            sx={{
              mr: 1,
              color: 'black',
              fontSize: '20px',
              width: '20px',
              height: '24px',
            }}
          />
          <Typography
            variant="textXL"
            sx={{ display: 'flex', flex: 1, justifyContent: 'left', alignItems: 'center' }}
          >
            {t('bulk.modalTitle')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ p: 0 }}
          >
            <X />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Typography
            id="pos-bulk-modal-description"
            sx={{
              textAlign: 'left',
              mb: 3,
              fontSize: { xs: '0.9rem', sm: '1rem' },
            }}
          >
            {t('bulk.modalDescription')}
          </Typography>
          <Grid
            item
            xs={12}
            sx={{ display: 'flex', justifyContent: 'left', columnGap: '10px' }}
          >
            <Button
              size="lg"
              variant="secondary"
              color="secondary"
              onClick={handleDownload}
              disabled={isExporting}
            >
              {isExporting ? t('bulk.exporting') : t('bulk.download')}
            </Button>
            <Button
              size="lg"
              color="secondary"
              onClick={() => document.getElementById('posMappingFileInput')?.click()}
              sx={{ marginRight: '8px' }}
            >
              {t('bulk.upload')}
            </Button>
          </Grid>
          <input
            aria-hidden
            type="file"
            id="posMappingFileInput"
            accept=".xlsx"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          {errorMessage && (
            <Typography
              color="error"
              sx={{ mt: 2, fontSize: 18 }}
            >
              {errorMessage}
            </Typography>
          )}

          {isSaving && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 18, color: 'text.secondary' }}>
                {t('bulk.validating')}
              </Typography>
            </Box>
          )}

          {successMessage && !isSaving && !validationErrors && (
            <Box sx={{ mt: 5, ml: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 23 }} />
              <Typography sx={{ fontSize: 16, color: 'success.main' }}>
                {successMessage}
              </Typography>
            </Box>
          )}

          {validationErrors && (
            <Box sx={{ mt: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography sx={{ px: 2, pt: 2, fontWeight: 600, fontSize: '0.95rem' }}>
                {t('bulk.validationTitle')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  sx={{ flex: 1 }}
                >
                  {VALIDATION_TABS.map((key) => (
                    <Tab
                      key={key}
                      label={`${t(`bulk.${key}` as any)} (${(validationErrors[key] ?? []).length})`}
                      sx={{ fontSize: '0.78rem', textTransform: 'none' }}
                    />
                  ))}
                </Tabs>
                <Tooltip
                  title={
                    <div style={{ padding: '4px', fontFamily: 'inherit' }}>
                      {VALIDATION_TABS.map((key, i) => (
                        <div key={key}>
                          <div style={{ fontWeight: 700, fontSize: '0.78rem', fontFamily: 'inherit' }}>
                            {t(`bulk.${key}` as any)}
                          </div>
                          <div style={{ fontSize: '0.75rem', fontFamily: 'inherit' }}>
                            {t(TAB_DESCRIPTION_KEYS[i] as any)}
                          </div>
                          {i < VALIDATION_TABS.length - 1 && (
                            <div style={{ height: '12px' }} />
                          )}
                        </div>
                      ))}
                    </div>
                  }
                  arrow
                  placement="left"
                >
                  <IconButton
                    size="medium"
                    sx={{ ml: 1, color: 'text.secondary' }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: '1.1rem', fontFamily: 'inherit' }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {VALIDATION_TABS.map((key, tabIndex) => (
                <Box
                  key={key}
                  hidden={activeTab !== tabIndex}
                  sx={{ px: 2, pb: 2 }}
                >
                  {activeTab === tabIndex && (
                    <PosMappingValidationTable
                      items={getTabItems(tabIndex)}
                      page={tabPages[tabIndex] ?? 0}
                      setPage={(p) => setTabPages(prev => ({ ...prev, [tabIndex]: p }))}
                      totalPages={getTotalPages(tabIndex)}
                      pageSize={ERRORS_PAGE_SIZE}
                    />
                  )}
                </Box>
              ))}
            </Box>
          )}

          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'right',
              columnGap: { xs: '8px', sm: '10px' },
              mt: 3,
            }}
          >
            <Button
              size="lg"
              variant="secondary"
              onClick={handleClose}
              sx={{ marginTop: { xs: '8px', sm: '0' } }}
            >
              {t('cancel')}
            </Button>
            <Button
              size="lg"
              onClick={handleSave}
              disabled={!selectedFile || !!errorMessage || isSaving || !!validationErrors}
              sx={{ marginTop: { xs: '8px', sm: '0' } }}
            >
              {isSaving ? <Loader /> : t('save')}
            </Button>
          </Grid>
        </DialogContent>

        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          open={showUploadSuccessMessage}
          onClose={handleCloseSnackbar}
          autoHideDuration={5000}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="success"
            variant="filled"
            sx={{ width: '100%' }}
          >
            {t('bulk.uploadSuccess')}
          </Alert>
        </Snackbar>
      </Dialog>
    </>
  );
};

export default PosMappingsBulkManage;